import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './ProgressModal.css';
import React, { useCallback, useEffect, useId } from "react";
import { useModal } from "./ModalContainer";
import { removeTimer, setIdTimer } from "../helpers/permanentIdTimer";
import Spinner from "../widgets/Spinner";
import { globalFocus } from "../helpers/dom";
const initialDelay = 75;
const defaultSuccessDuration = 500;
const hideDuration = 300;
export const ProgressModal = ({ status = 'show', showSpinner = true, successMessage, message, onDisappeared }) => {
    const ref = React.createRef();
    const [visible, setVisible] = React.useState(false);
    useEffect(() => {
        var _a, _b;
        if (status != 'hidden') {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.showModal();
            setVisible(true);
        }
        else {
            (_b = ref.current) === null || _b === void 0 ? void 0 : _b.close();
            if (visible)
                onDisappeared === null || onDisappeared === void 0 ? void 0 : onDisappeared();
            setVisible(false);
        }
        return () => {
            var _a;
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.close();
            if (visible)
                onDisappeared === null || onDisappeared === void 0 ? void 0 : onDisappeared();
        };
    }, [status, ref.current]);
    return (_jsxs("dialog", { className: `progressModal state-${status}`, ref: ref, children: [showSpinner && _jsx(Spinner, { spinning: status !== 'success' }), _jsxs("div", { className: 'messages', children: [_jsx("div", { className: `message ${status === 'show' ? 'show' : ''}`, children: message }), _jsxs("div", { className: `errorMessage ${status === 'timeout' ? 'show' : ''}`, children: ["Sorry, this is taking longer than expected", _jsx("br", {}), "Please try reloading the page."] }), _jsx("div", { className: `successMessage ${status === 'success' ? 'show' : ''}`, children: successMessage })] })] }));
};
export function useProgressModal(props = {}) {
    const [returnCallback] = React.useState([() => { }, () => { }]);
    const focusElementRef = React.useRef(undefined);
    const [effectiveProps, setEffectiveProps] = React.useState(props);
    const [wasVisible, setWasVisible] = React.useState(false);
    const timerId = useId();
    const [showModal, dismissModal, modifyModal] = useModal(ProgressModal, {
        ...props,
        onDisappeared: () => { dismissModal(); globalFocus(focusElementRef.current); },
    });
    const show = useCallback((dynamicProps) => {
        const p = { ...props, ...dynamicProps, showSpinner: true, status: 'hidden' };
        setWasVisible(false);
        setEffectiveProps(p);
        focusElementRef.current = p.focusId || document.activeElement;
        setIdTimer(timerId, initialDelay, () => {
            showModal(p);
            setWasVisible(true);
            modifyModal({ status: 'show' });
            if (p.timeout)
                setIdTimer(timerId, p.timeout, () => { modifyModal({ status: 'timeout' }); });
            else
                removeTimer(timerId);
        });
    }, [props, showModal, modifyModal, timerId]);
    const hide = useCallback((success = false, closeFocusId, successMessageOverride) => {
        var _a;
        if (closeFocusId)
            focusElementRef.current = closeFocusId;
        const successMessage = successMessageOverride !== null && successMessageOverride !== void 0 ? successMessageOverride : effectiveProps.successMessage;
        if (success === true && successMessage) {
            if (wasVisible) {
                modifyModal({ status: 'success', successMessage });
            }
            else {
                showModal({ ...effectiveProps, successMessage, showSpinner: false, status: 'success' });
            }
            setIdTimer(timerId, (_a = effectiveProps.successDuration) !== null && _a !== void 0 ? _a : defaultSuccessDuration, () => {
                modifyModal({ status: 'hiding' });
                setIdTimer(timerId, hideDuration, () => { dismissModal(); });
            });
        }
        else {
            removeTimer(timerId);
            modifyModal({ status: 'hiding' });
            setIdTimer(timerId, hideDuration, () => { dismissModal(); });
        }
    }, [effectiveProps, wasVisible, modifyModal, dismissModal, timerId]);
    // modify existing array so hide is always using the latest effective props
    returnCallback[0] = show;
    returnCallback[1] = hide;
    return returnCallback;
}
;
export default ProgressModal;
